import {
  NavLink,
  Outlet,
  useLocation,
  useMatches,
  type UIMatch,
} from 'react-router-dom'

import SiteWorksLogo from '@/assets/SiteWorks_Logo.svg'
import SouthernWaterLogo from '@/assets/Southern_Water_Logo.svg'
import DevicesIcon from '@/assets/icons/devices.svg'
import JobsIcon from '@/assets/icons/jobs.svg'
import ReportsIcon from '@/assets/icons/reports.svg'
import { BuildInfo } from '@/features/version'
import { cn } from '@/lib/utils'
import { AppLayoutHeader } from '@/routes/app-layout-header'

type NavItem = {
  label: string
  to: string
  icon: string
  isActive?: (pathname: string) => boolean
}

const navItems: NavItem[] = [
  {
    label: 'Jobs',
    to: '/',
    icon: JobsIcon,
    isActive: (pathname) => pathname === '/' || pathname.startsWith('/jobs'),
  },
  { label: 'Devices', to: '/devices', icon: DevicesIcon },
  {
    label: 'Reports',
    to: '/reports',
    icon: ReportsIcon,
    isActive: (pathname) => pathname === '/reports' || pathname.startsWith('/reports/'),
  },
]

type NavItemIconProps = {
  src: string
  active: boolean
}

const NavItemIcon = ({ src, active }: NavItemIconProps) => (
  <span
    aria-hidden
    className={cn('size-5 shrink-0', active ? 'bg-white' : 'bg-primary-dark')}
    style={{
      maskImage: `url(${JSON.stringify(src)})`,
      WebkitMaskImage: `url(${JSON.stringify(src)})`,
      maskSize: 'contain',
      WebkitMaskSize: 'contain',
      maskRepeat: 'no-repeat',
      WebkitMaskRepeat: 'no-repeat',
      maskPosition: 'center',
      WebkitMaskPosition: 'center',
    }}
  />
)

export type AppRouteHandle = {
  title: string
  breadcrumbs?: { label: string; to?: string }[]
}

const getRouteHandle = (matches: UIMatch[]): AppRouteHandle => {
  const match = [...matches]
    .reverse()
    .find(
      (item) =>
        item.handle &&
        typeof item.handle === 'object' &&
        'title' in item.handle
    )

  const handle = match?.handle as AppRouteHandle | undefined

  return {
    title: handle?.title ?? 'SiteWorks Portal',
    breadcrumbs: handle?.breadcrumbs ?? [],
  }
}

const AppLayoutSidebar = () => {
  const { pathname } = useLocation()

  return (
    <aside className="flex w-[clamp(14rem,18vw,20rem)] shrink-0 flex-col bg-background">
      <div className="px-6 pt-8">
        <img
          src={SiteWorksLogo}
          alt="SiteWorks"
          className="w-full"
        />
      </div>

      <div
        className="mx-6 my-8 border-t border-muted-foreground/20"
        role="presentation"
      />

      <nav aria-label="Main" className="flex flex-1 flex-col gap-3 px-4">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) => {
              const active = item.isActive ? item.isActive(pathname) : isActive

              return cn(
                'inline-flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-colors',
                active
                  ? 'bg-foreground text-white'
                  : 'bg-surface-muted text-foreground hover:bg-surface-muted/60'
              )
            }}
          >
            {({ isActive }) => {
              const active = item.isActive ? item.isActive(pathname) : isActive

              return (
                <>
                  <NavItemIcon src={item.icon} active={active} />
                  {item.label}
                </>
              )
            }}
          </NavLink>
        ))}
      </nav>

      <div className="px-6 pb-3 pt-4">
        <img
          src={SouthernWaterLogo}
          alt="Southern Water"
          className="h-[42px] w-auto"
        />
        <BuildInfo />
      </div>
    </aside>
  )
}

export const AppLayout = () => {
  const matches = useMatches()
  const { title, breadcrumbs } = getRouteHandle(matches)

  return (
    <div className="flex min-h-dvh">
      <AppLayoutSidebar />

      <div className="flex min-w-0 flex-1 flex-col">
        <AppLayoutHeader title={title} breadcrumbs={breadcrumbs} />

        <main className="flex-1 overflow-auto px-8 pb-8">
          <div className="bg-background p-2 rounded-xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
